
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Mail, Send } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

const ContactForm = () => {
  const { t } = useTranslation('common');
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
    type: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Appel à l'Edge Function Supabase
      const { data, error } = await supabase.functions.invoke('send-contact', {
        body: formData
      });
      
      if (error) {
        throw new Error(error.message);
      }
      
      // Réinitialiser le formulaire
      setFormData({
        name: "",
        email: "",
        subject: "",
        message: "",
        type: ""
      });

      toast({
        title: t('help.contact.form.success.title'),
        description: t('help.contact.form.success.description'),
      });
    } catch (error) {
      console.error("Erreur lors de l'envoi du message:", error);
      toast({
        title: t('help.contact.form.error.title'),
        description: t('help.contact.form.error.description'),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mail className="h-6 w-6 text-golf-600" />
          {t('help.contact.title')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="mb-4">
          <p className="text-sm text-muted-foreground mb-2">
            {t('help.contact.description')}
          </p>
          <p className="text-sm text-muted-foreground">
            {t('help.contact.directEmail')} : <strong>contact@golf-progress-calculator.com</strong>
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">{t('help.contact.form.name')} *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                placeholder={t('help.contact.form.namePlaceholder')}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">{t('help.contact.form.email')} *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange("email", e.target.value)}
                placeholder={t('help.contact.form.emailPlaceholder')}
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="type">{t('help.contact.form.type')} *</Label>
            <Select value={formData.type} onValueChange={(value) => handleChange("type", value)} required>
              <SelectTrigger>
                <SelectValue placeholder={t('help.contact.form.typePlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="bug">{t('help.contact.form.messageTypes.bug')}</SelectItem>
                <SelectItem value="feature">{t('help.contact.form.messageTypes.feature')}</SelectItem>
                <SelectItem value="help">{t('help.contact.form.messageTypes.help')}</SelectItem>
                <SelectItem value="feedback">{t('help.contact.form.messageTypes.feedback')}</SelectItem>
                <SelectItem value="other">{t('help.contact.form.messageTypes.other')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="subject">{t('help.contact.form.subject')} *</Label>
            <Input
              id="subject"
              value={formData.subject}
              onChange={(e) => handleChange("subject", e.target.value)}
              placeholder={t('help.contact.form.subjectPlaceholder')}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="message">{t('help.contact.form.message')} *</Label>
            <Textarea
              id="message"
              value={formData.message}
              onChange={(e) => handleChange("message", e.target.value)}
              placeholder={t('help.contact.form.messagePlaceholder')}
              rows={6}
              required
            />
          </div>

          <Button 
            type="submit" 
            className="w-full" 
            disabled={isSubmitting}
          >
            <Send className="h-4 w-4 mr-2" />
            {isSubmitting ? t('help.contact.form.sending') : t('help.contact.form.sendButton')}
          </Button>
        </form>

        <div className="mt-4 p-3 bg-blue-50 rounded-md">
          <p className="text-sm text-blue-800">
            <strong>Note :</strong> {t('help.contact.form.note')}
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default ContactForm;
